import { getIcon } from "../assets/icons.js";
import { ELEMENT_COLORS } from "../assets/elements.js";


const DIAMOND_ICONS = ['light', 'cold', 'water', 'lightning'];
const TILE_THEMES = {
    fire: { color: "hsl(10, 90%, 55%)" },
    earth: { color: "hsl(40, 100%, 40%)" },
    light: { color: "hsl(60, 100%, 80%)" },
    wind: { color: "hsl(120, 80%, 60%)" },
    cold: { color: "hsl(185, 80%, 75%)" },
    water: { color: "hsl(225, 90%, 55%)" },
    lightning: { color: "hsl(275, 70%, 80%)" },
    dark: { color: "hsl(275, 40%, 30%)" }
};

function integerToRoman(num) {
    const romanValues = {
        M: 1000,
        CM: 900,
        D: 500,
        CD: 400,
        C: 100,
        XC: 90,
        L: 50,
        XL: 40,
        X: 10,
        IX: 9,
        V: 5,
        IV: 4,
        I: 1
    };
    let roman = '';
    for (let key in romanValues) {
        while (num >= romanValues[key]) {
            roman += key;
            num -= romanValues[key];
        }
    }
    return roman;
}

export const elementFactory = {
    createHuntingSpellIcons(spellsData) {
        const spellButtons = [];
        spellsData.forEach(spell => {
            const spellNode = document.createElement("div");
            spellNode.className = `hunting-spell hunting-spell-${spell.elementName.toLowerCase()}`;
            spellNode.id = spell.id;
            spellNode.setAttribute("data-element", spell.elementName);

            // Rule Check: If it's the 'all' grid profile, do not draw an icon template inside
            if (spell.elementName.toLowerCase() !== "all") {
                // Request icon shell targeting a clean pseudo object template
                const svgIcon = getIcon(spell.elementName)
                //console.log(spellNode);

                const theme = ELEMENT_COLORS[spell.elementName] || "#fff";

                // Use setProperty with an important flag to override any background settings in your CSS stylesheet
                spellNode.style.backgroundColor = theme;

                // Request icon string directly using just the string element name
                const svgString = getIcon(spell.elementName)

                if (svgString) {
                    // 1. Inject the raw SVG string safely on the fly
                    spellNode.insertAdjacentHTML("beforeend", svgString);

                    // 2. Query the parsed vector node and force the inner paths to be white
                    const svgIcon = spellNode.querySelector("svg");
                    if (svgIcon) {
                        svgIcon.removeAttribute("style");
                        svgIcon.querySelectorAll("path, use, rect, circle").forEach(child => {
                            child.removeAttribute("fill");
                            child.removeAttribute("stroke");
                            // Force the inner icon vectors to be pure white (#eee)
                            child.style.setProperty("fill", "#eee", "important");
                            child.style.setProperty("stroke", "none", "important");
                        });
                    }
                }
            } else {
                // "all" tile explicitly renders as a solid white icon surface block frame
                spellNode.style.backgroundColor = "#eee";
            }

            spellButtons.push(spellNode);
        });

        return spellButtons
    },

    createCultivationDropButtons(elementsData) {
        const buttons = [];

        //Check if player has no elements unlocked
        if (!elementsData || Object.keys(elementsData).length === 0) {
            const noElementsBtn = document.createElement('div');
            noElementsBtn.className = 'cultivation-drop-button static-option';
            noElementsBtn.id = 'none-cultivation-drop-button';
            noElementsBtn.textContent = 'NO ELEMENTS LOADED';
            noElementsBtn.dataset.element = 'none';

            buttons.push(noElementsBtn);
            return buttons;
        }

        for (const [elementId, elementDetails] of Object.entries(elementsData)) {
            const btn = document.createElement('div');
            btn.className = 'cultivation-drop-button';
            btn.id = `${elementId}-cultivation-drop-button`;
            btn.textContent = elementDetails.name.toUpperCase();
            btn.dataset.element = elementId;

            buttons.push(btn);
        }

        return buttons;
    },

    createFormationElementButtons(elementsData) {
        const buttons = [];
        //Check if player has no elements unlocked
        if (!elementsData || Object.keys(elementsData).length === 0) {
            const noElementsBtn = document.createElement('div');
            noElementsBtn.className = 'formation-element-button static-option';
            noElementsBtn.id = 'none-formation-button';
            noElementsBtn.textContent = 'NO ELEMENTS LOADED';
            noElementsBtn.dataset.element = 'none';

            buttons.push(noElementsBtn);
            return buttons;
        }

        for (const [elementId, elementDetails] of Object.entries(elementsData)) {
            const btn = document.createElement('div');
            btn.className = 'formation-element-button';
            btn.id = `${elementId}-formation-button`;
            btn.textContent = elementDetails.name.toUpperCase();
            btn.innerHTML = getIcon(elementId);

            const svgNode = btn.querySelector('svg');
            if (svgNode) svgNode.classList.add('formation-icon');

            const theme = ELEMENT_COLORS[elementId] || "#fff" ;

            btn.style.setProperty('--element-color', theme);
            btn.style.color = "#fff";

            const shapes = btn.querySelectorAll(".formation-icon *");
            shapes.forEach(shape => shape.style.fill = "var(--element-color)");

            btn.dataset.element = elementId;

            buttons.push(btn);
        }

        return buttons;
    },

    createSpellbookElementButtons(elementsData) {
        const buttons = [];
        //Check if player has no elements unlocked

        const noElementsBtn = document.createElement('div');
        noElementsBtn.className = 'spellbook-element-button static-option';
        noElementsBtn.id = 'none-spellbook-button';
        noElementsBtn.textContent = 'O';
        noElementsBtn.dataset.element = 'all';
        buttons.push(noElementsBtn);
        if (!elementsData || Object.keys(elementsData).length === 0) {
            return buttons;
        }

        for (const [elementId, elementDetails] of Object.entries(elementsData)) {
            const btn = document.createElement('div');
            btn.className = 'spellbook-element-button';
            btn.id = `${elementId}-spellbook-button`;
            btn.textContent = elementDetails.name.toUpperCase();
            btn.innerHTML = getIcon(elementId);

            const svgNode = btn.querySelector('svg');
            if (svgNode) svgNode.classList.add('spellbook-icon');

            const theme = ELEMENT_COLORS[elementId] || "#fff";

            btn.style.setProperty('--element-color', theme);
            btn.style.color = "#fff";

            const shapes = btn.querySelectorAll(".spellbook-icon *");
            shapes.forEach(shape => shape.style.fill = "var(--element-color)");

            btn.dataset.element = elementId;

            buttons.push(btn);
        }

        return buttons;
    },


    createSpellGridSlots(size) {
        const slots = [];
        let numSlots = Math.pow(size, 2);
        for (let i = 0; i < numSlots; i++) {
            const slot = document.createElement('div');
            slot.className = "spell-grid-slot";
            slot.dataset.yIndex = Math.floor(i / size);
            slot.dataset.xIndex = i % size;

            slots.push(slot);
        }

        return slots
    },

    createSpellShardInventorySlots(amount) {
        const slots = [];
        let roundedAmount = Math.max(Math.ceil(amount / 8) * 8, 40);
        for (let i = 0; i < roundedAmount; i++) {
            const slot = document.createElement('div');
            slot.className = "spell-inventory-slot";
            slot.dataset.index = i;
            slots.push(slot);
        }

        return slots
    },

    createTile(tile, isCenter, size = 5, existingCell = null, playersOnTile = []) {
        const cell = existingCell || document.createElement("div");

        if (tile !== "empty") {
            const elementName = tile.stats.element.toLowerCase();
            const theme = ELEMENT_COLORS[elementName] || "#fff" ;

            cell.style.setProperty('--element-color', theme);
                
                let numPlayers = integerToRoman(playersOnTile.length);
         
                cell.className = isCenter ? "grid-item-center" : "grid-item";

                // CHECK BOTH: Does the cell exist AND does it contain a valid layout?
                const hasLayout = existingCell && cell.querySelector(".tile-coords");

                if (hasLayout) {
                    cell.querySelector(".tile-coords").textContent = `(${tile.xPos}, ${tile.yPos})`;
                    //cell.querySelector(".tile-stat-left").textContent = tile.stats.strengthLevel;
                    cell.querySelector(".tile-stat-right").textContent = `${numPlayers}`;
                    cell.querySelector(".tile-icon use").setAttribute("href", `#icon-${elementName}`);
                } else {
                    // Rebuild the template if it was blanked out by an empty slot
                    cell.innerHTML = `
                        <div class="tile-row top-row">
                            <div class="tile-coords">(${tile.xPos}, ${tile.yPos})</div>
                            <svg class="tile-icon" viewBox="0 0 400 400">
                                <use href="#icon-${elementName}"></use>
                            </svg>
                        </div>
                        
                        <div class="tile-row bottom-row">
                            <div class="tile-stat-left"></div>
                            <div class="tile-stat-right">${numPlayers}</div>
                        </div>
                    `;
                }

                cell.dataset.x = tile.xPos;
                cell.dataset.y = tile.yPos;
            
        } else {
            // Turning a tile into an empty edge-of-world slot safely
            cell.className = "grid-item empty-world-slot";
            cell.innerHTML = "";
            cell.style.removeProperty('--element-color');
            cell.dataset.x = "null";
            cell.dataset.y = "null";
        }

        return cell;
    }
};




